/* eslint-disable @typescript-eslint/no-explicit-any */
/** @jsx jsx */
import { jsx } from "@emotion/react";
import { SelectInputProps } from "@material-ui/core/Select/SelectInput";
import React, { FC, Ref } from "react";
import cx from "classnames";
import Input from "@material-ui/core/Input";
import Select from "@material-ui/core/Select";
import { AcceptableSelectOnChange, Option, SelectedOptionValue } from "./types";
import { EMPTY_OPTION } from "../../../../globalConsts";
import { flowioSelectCss, placeholderCss } from "./styles";
import SelectItem from "./SelectItem";
import { NonNativeSection } from "./NonNativeSection";

const FlowioSelectInput = <Input disableUnderline />;

const FlowioSelect: FC<Props> = (props) => {
  const {
    options: optionsIn,
    value: valueIn,
    defaultValue: defaultValueIn,
    placeholder,
    onChange,
    onBlur,
    inputProps,
    disabled = false,
    className,
    inputRef,
    name,
    id,
    required = false,
    onClick,
    emptyOption = false,
    native = true,
    onOpen,
  } = props;

  const options = emptyOption ? [EMPTY_OPTION, ...optionsIn] : optionsIn;
  const fallbackValue = options[0] ?? "";

  const isControlled = valueIn !== undefined;
  const value = isControlled ? valueIn ?? fallbackValue : undefined;
  const defValue = !isControlled ? defaultValueIn ?? fallbackValue : undefined;
  const hasError = required && !valueIn;

  const handleChange = (
    e: React.ChangeEvent<{ name?: string; value: unknown }>
  ): void => {
    if (!onChange) return;
    onChange((e.target.value || null) as any);
  };

  return (
    <div css={{ position: "relative" }}>
      <Select
        onChange={handleChange}
        onBlur={onBlur}
        value={value}
        inputRef={inputRef}
        defaultValue={defValue}
        input={FlowioSelectInput}
        native={native}
        placeholder={placeholder}
        renderValue={(v) => {
          const selectedOption = options.find((o) => o.value === v);
          if (selectedOption?.value) return selectedOption.name;
          return <span css={placeholderCss}>{placeholder || ""}</span>;
        }}
        inputProps={{ name, onClick, id, ...inputProps } as any}
        disabled={disabled}
        className={cx("basic-select", className)}
        css={flowioSelectCss(hasError)}
        name={name}
        autoWidth
        required={required}
        data-cy="input flowio-select-input"
        onOpen={onOpen}
      >
        {options.map((option) => (
          <SelectItem
            value={option.value}
            key={option.value}
            nativeSelectMode={native}
          >
            {option.name}
          </SelectItem>
        ))}

        {props.native === false && <NonNativeSection {...props} />}
      </Select>
    </div>
  );
};

// Value related

export type UncontrolledOnlyProps = {
  value?: undefined;
  defaultValue: SelectedOptionValue;
  inputRef: Ref<HTMLInputElement>;
};

export type ControlledOnlyProps = {
  value: SelectedOptionValue;
  defaultValue?: undefined;
  inputRef?: undefined;
};

export type RefControlledOnlyProps = {
  value?: undefined;
  defaultValue?: undefined | string;
  inputRef?: Ref<HTMLInputElement>;
};

export type ValueRelatedProps =
  | ControlledOnlyProps
  | UncontrolledOnlyProps
  | RefControlledOnlyProps;

// Native related + additional nonative functionality

export type NativeProps = {
  native?: true;
};

export type NonNativeProps = {
  native: false;
  loading?: boolean;
  emptyListMessage?: string;
};

export type NativeRelatedProps = NativeProps | NonNativeProps;

// Props

type Props = ValueRelatedProps &
  NativeRelatedProps & {
    options: Option[];
    onChange?: AcceptableSelectOnChange;
    onOpen?(): void;
    onBlur?(): void;
    inputProps?: SelectInputProps;
    disabled?: boolean;
    className?: string;
    name?: string | undefined;
    onClick?: () => void;
    placeholder?: string;
    required?: boolean;
    id?: string;
    emptyOption?: boolean;
  };

export default FlowioSelect;
export type FlowioSelectProps = Props;
